import { MetaDescriptor } from "@remix-run/node";

interface MetaHelperOptions {
  title: string;
  description?: string;
  image?: string | null;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageType?: string;
  url: string;
  locale: string;
  type?: "website" | "article" | "profile";
  keywords?: string;
  slug?: string;
  alternateLanguages?: {
    en: string;
    fr: string;
  };
}

export const getCentralizedMeta = ({
  title,
  description = "",
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  imageType,
  url,
  locale,
  type = "website",
  keywords = "",
  alternateLanguages,
}: MetaHelperOptions): MetaDescriptor[] => {
  const meta: MetaDescriptor[] = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:site_name", content: "La Ruche Health" },
    {
      property: "og:locale",
      content: locale.toLowerCase().startsWith("fr") ? "fr_FR" : "en_GB",
    },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "keywords", content: keywords },
  ];

  if (image) {
    meta.push({ property: "og:image", content: image });
    if (image.startsWith("https://")) {
      meta.push({ property: "og:image:secure_url", content: image });
    }
    meta.push({ name: "twitter:image", content: image });
    if (imageWidth) {
      meta.push({ property: "og:image:width", content: String(imageWidth) });
    }
    if (imageHeight) {
      meta.push({ property: "og:image:height", content: String(imageHeight) });
    }
    if (imageType) {
      meta.push({ property: "og:image:type", content: imageType });
    }
    if (imageAlt) {
      meta.push({ property: "og:image:alt", content: imageAlt });
      meta.push({ name: "twitter:image:alt", content: imageAlt });
    }
  }

  // Canonical link
  meta.push({
    tagName: "link",
    rel: "canonical",
    href: url,
  });

  // Alternate language links
  if (alternateLanguages) {
    meta.push({
      tagName: "link",
      rel: "alternate",
      hrefLang: "en",
      href: alternateLanguages.en,
    });
    meta.push({
      tagName: "link",
      rel: "alternate",
      hrefLang: "fr",
      href: alternateLanguages.fr,
    });
    meta.push({
      tagName: "link",
      rel: "alternate",
      hrefLang: "x-default",
      href: alternateLanguages.fr, // French is the app fallback language
    });
  }

  return meta;
};
